import {
  activeMeasurementIdAtom,
  addMeasurementAtom,
  addPointModeAtom,
  fragmentPickFnAtom,
  meshPickTargetsAtom,
  pointCloudTargetsAtom,
  updateMeasurementPointsAtom,
} from '@/atoms';
import { FeaturesQuery } from '@/graphql/graphql';
import type { SnapFeature } from '@/lib/snapping/candidates';
import { attachRightClickCancel } from '@/lib/rightClickCancel';
import { Billboard, Text } from '@react-three/drei';
import { ThreeEvent, useFrame, useThree } from '@react-three/fiber';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as THREE from 'three';
import { pickSurface } from './pickSurface';
import PointX from './PointX';
import { getUnitsPerPixel } from './screenScale';
import { useSnapResolver } from './useSnapResolver';

/** Slim cylinder drawn between two world-space points. */
const CylinderLine = ({
  start,
  end,
  color,
  opacity = 0.8,
  radius = 0.005,
}: {
  start: THREE.Vector3;
  end: THREE.Vector3;
  color: string;
  opacity?: number;
  radius?: number;
}) => {
  const { midpoint, length, quaternion } = useMemo(() => {
    const mid = start.clone().add(end).multiplyScalar(0.5);
    const len = start.distanceTo(end);
    const dir = new THREE.Vector3().subVectors(end, start).normalize();
    const quat = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
    return {
      midpoint: mid.toArray() as [number, number, number],
      length: len,
      quaternion: [quat.x, quat.y, quat.z, quat.w] as [number, number, number, number],
    };
  }, [start, end]);

  if (length < 1e-6) return null;

  return (
    <mesh position={midpoint} quaternion={quaternion} renderOrder={Number.MAX_SAFE_INTEGER - 100}>
      <cylinderGeometry args={[radius, radius, length, 8, 1]} />
      <meshBasicMaterial color={color} transparent opacity={opacity} depthTest={false} />
    </mesh>
  );
};

const MeasuringTool = ({
  projectId,
  features = [],
}: {
  projectId?: string;
  features?: FeaturesQuery['featuresByLayer'][number][];
}) => {
  const [pickedPoints, setPickedPoints] = useState<THREE.Vector3[]>([]);
  const [hoverPoint, setHoverPoint] = useState<THREE.Vector3 | null>(null);
  const camera = useThree((state) => state.camera);
  const renderer = useThree((state) => state.gl);
  const size = useThree((state) => state.size);
  const raycaster = useMemo(() => new THREE.Raycaster(), []);
  const pcoTargets = useAtomValue(pointCloudTargetsAtom);

  const getVisibleFeatures = useCallback<() => SnapFeature[]>(
    () => features.map((f) => ({ id: f.id, geometry: f.geometry as SnapFeature['geometry'] })),
    [features],
  );
  const resolve = useSnapResolver(getVisibleFeatures);
  const meshTargets = useAtomValue(meshPickTargetsAtom);
  const fragmentPick = useAtomValue(fragmentPickFnAtom);
  const [params] = useSearchParams();
  const showMeasurements = params.get('showMeasurements') === 'true';
  const addPointMode = useAtomValue(addPointModeAtom);
  const addMeasurement = useSetAtom(addMeasurementAtom(projectId ?? ''));
  const updateMeasurementPoints = useSetAtom(updateMeasurementPointsAtom(projectId ?? ''));
  const setActiveMeasurementId = useSetAtom(activeMeasurementIdAtom);
  const savedIdRef = useRef<string | null>(null);
  const hasSavedRef = useRef(false);

  const saveMeasurement = useCallback(
    (points: THREE.Vector3[]) => {
      if (!projectId || points.length < 2) return;
      const id = addMeasurement({
        type: 'distance',
        points: points.map((p) => p.toArray() as [number, number, number]),
      });
      savedIdRef.current = id ?? null;
      setActiveMeasurementId(id ?? null);
    },
    [projectId, addMeasurement],
  );

  const pointerDownPos = useRef<{ x: number; y: number } | null>(null);

  // refs for dragging state and group references (changed from sphere to group)
  const draggingRef = useRef<{
    index: number;
    plane: THREE.Plane;
    offset: THREE.Vector3;
  } | null>(null);

  // Hover handler - show preview marker
  useEffect(() => {
    if (!showMeasurements || addPointMode?.enabled) return;

    const handleMouseMove = async (e: MouseEvent) => {
      // Calculate normalized device coordinates
      const rect = renderer.domElement.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

      // Configure raycaster
      raycaster.setFromCamera(new THREE.Vector2(x, y), camera);
      const hit = pickSurface(renderer, camera, raycaster, pcoTargets, meshTargets);

      // Resolve the surface hit, falling back to an async IFC fragment pick.
      // Either may be absent — the snap resolver can still snap to vector
      // geometry in screen space, so we pass null when there's no surface and
      // only clear the preview when the resolver also returns 'none'.
      let surfaceHit: [number, number, number] | null = hit
        ? [hit.position.x, hit.position.y, hit.position.z]
        : null;
      if (!surfaceHit && fragmentPick) {
        const pt = await fragmentPick(camera, new THREE.Vector2(e.clientX, e.clientY), renderer.domElement);
        if (pt) surfaceHit = [pt.x, pt.y, pt.z];
      }
      const cursorPx: [number, number] = [((x + 1) / 2) * size.width, ((1 - y) / 2) * size.height];
      const snap = resolve(cursorPx, surfaceHit);
      if (snap.kind !== 'none') {
        setHoverPoint(new THREE.Vector3(...snap.position));
        renderer.domElement.style.cursor = 'crosshair';
      } else {
        setHoverPoint(null);
        renderer.domElement.style.cursor = 'default';
      }
    };

    renderer.domElement.addEventListener('pointermove', handleMouseMove);
    return () => {
      renderer.domElement.removeEventListener('pointermove', handleMouseMove);
      renderer.domElement.style.cursor = 'default';
    };
  }, [showMeasurements, addPointMode, pcoTargets, meshTargets, camera, renderer, raycaster, fragmentPick, size, resolve]);

  useEffect(() => {
    if (!showMeasurements) return;
    const dom = renderer.domElement;
    const DRAG_THRESHOLD = 5;

    const handlePointerDown = (e: MouseEvent) => {
      pointerDownPos.current = { x: e.clientX, y: e.clientY };
    };

    const handlePointerUp = async (e: MouseEvent) => {
      // Only react to left-click
      if (e.button !== 0) return;
      // Ignore camera drags — only react to clicks
      if (!pointerDownPos.current) return;
      const dx = e.clientX - pointerDownPos.current.x;
      const dy = e.clientY - pointerDownPos.current.y;
      pointerDownPos.current = null;
      if (Math.sqrt(dx * dx + dy * dy) > DRAG_THRESHOLD) return;

      if (addPointMode?.enabled) return;
      if (draggingRef.current) return;

      const rect = dom.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

      raycaster.setFromCamera(new THREE.Vector2(x, y), camera);
      let hit = pickSurface(renderer, camera, raycaster, pcoTargets, meshTargets);

      // Fallback: try IFC fragment raycast
      if (!hit && fragmentPick) {
        const pt = await fragmentPick(camera, new THREE.Vector2(e.clientX, e.clientY), renderer.domElement);
        if (pt) hit = { position: pt, layerId: null };
      }

      // No surface hit is fine — try a screen-space snap to existing geometry.
      // Bail only when there's nothing to place on (no surface, no snap).
      const cursorPx: [number, number] = [((x + 1) / 2) * size.width, ((1 - y) / 2) * size.height];
      const snap = resolve(cursorPx, hit ? [hit.position.x, hit.position.y, hit.position.z] : null);
      if (snap.kind === 'none') return;
      const world = new THREE.Vector3(...snap.position);
      setPickedPoints((prev) => {
        if (prev.length === 2) {
          hasSavedRef.current = false;
          savedIdRef.current = null;
          setActiveMeasurementId(null);
        }
        const pts = prev.length === 2 ? [] : prev;
        return [...pts, world];
      });

      // Clear hover point when a point is picked
      setHoverPoint(null);
    };

    dom.addEventListener('pointerdown', handlePointerDown);
    dom.addEventListener('pointerup', handlePointerUp);
    return () => {
      dom.removeEventListener('pointerdown', handlePointerDown);
      dom.removeEventListener('pointerup', handlePointerUp);
    };
  }, [pcoTargets, meshTargets, camera, renderer, raycaster, addPointMode, showMeasurements, fragmentPick, size, resolve]);

  useEffect(() => {
    if (pickedPoints.length === 2 && !hasSavedRef.current) {
      hasSavedRef.current = true;
      saveMeasurement(pickedPoints);
    }
  }, [pickedPoints, saveMeasurement]);

  useEffect(() => {
    if (!showMeasurements) {
      hasSavedRef.current = false;
      savedIdRef.current = null;
      setActiveMeasurementId(null);
      setPickedPoints([]);
      setHoverPoint(null);
    }
  }, [showMeasurements]);

  // Right-click cancels the current measurement
  useEffect(() => {
    if (!showMeasurements) return;
    // A right-*drag* (camera pan) must not cancel — see attachRightClickCancel.
    return attachRightClickCancel(renderer.domElement, () => {
      hasSavedRef.current = false;
      savedIdRef.current = null;
      setActiveMeasurementId(null);
      setPickedPoints([]);
      setHoverPoint(null);
    });
  }, [showMeasurements, renderer]);

  const startDrag = (e: ThreeEvent<PointerEvent>, index: number) => {
    e.stopPropagation();
    const point = e.point.clone();
    const camDir = new THREE.Vector3().copy(camera.getWorldDirection(new THREE.Vector3()));
    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(camDir, point);
    const offset = point.clone().sub(pickedPoints[index]);
    draggingRef.current = { index, plane, offset };

    const dom = renderer.domElement;

    const onPointerMove = (ev: PointerEvent) => {
      ev.stopPropagation();
      const rect = dom.getBoundingClientRect();
      const x = ((ev.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((ev.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(new THREE.Vector2(x, y), camera);
      const intersectPoint = new THREE.Vector3();
      if (raycaster.ray.intersectPlane(plane, intersectPoint)) {
        setPickedPoints((prev) => {
          const pts = [...prev];
          pts[index] = intersectPoint.clone().sub(offset);
          if (savedIdRef.current) {
            updateMeasurementPoints({
              id: savedIdRef.current,
              points: pts.map((p) => p.toArray() as [number, number, number]),
            });
          }
          return pts;
        });
      }
    };

    const onPointerUp = () => {
      window.removeEventListener('pointermove', onPointerMove, true);
      window.removeEventListener('pointerup', onPointerUp);

      const hit = pickSurface(renderer, camera, raycaster, pcoTargets, meshTargets);

      if (hit) {
        const world = hit.position.clone();
        setPickedPoints((prev) => {
          const pts = [...prev];
          pts[index] = world;
          if (savedIdRef.current) {
            updateMeasurementPoints({
              id: savedIdRef.current,
              points: pts.map((p) => p.toArray() as [number, number, number]),
            });
          }
          return pts;
        });
      } else if (savedIdRef.current) {
        setPickedPoints((prev) => {
          if (savedIdRef.current) {
            updateMeasurementPoints({
              id: savedIdRef.current,
              points: prev.map((p) => p.toArray() as [number, number, number]),
            });
          }
          return prev;
        });
      }

      draggingRef.current = null;
    };

    window.addEventListener('pointermove', onPointerMove, true);
    window.addEventListener('pointerup', onPointerUp);
  };

  // Create triangle points for height visualization (world-space)
  const trianglePoints = useMemo(() => {
    if (pickedPoints.length < 2) return [];

    const baseZ = Math.min(pickedPoints[0].z, pickedPoints[1].z);
    const lowerPoint = pickedPoints[0].z < pickedPoints[1].z ? pickedPoints[0] : pickedPoints[1];
    const higherPoint = pickedPoints[0].z < pickedPoints[1].z ? pickedPoints[1] : pickedPoints[0];
    const cornerPoint = new THREE.Vector3(higherPoint.x, higherPoint.y, baseZ);

    return [lowerPoint.clone(), cornerPoint, higherPoint.clone()];
  }, [pickedPoints]);

  const heightLinePoints = useMemo(() => {
    if (pickedPoints.length < 2) return [];
    const baseZ = Math.min(pickedPoints[0].z, pickedPoints[1].z);
    const higherPoint = pickedPoints[0].z < pickedPoints[1].z ? pickedPoints[1] : pickedPoints[0];
    const cornerPoint = new THREE.Vector3(higherPoint.x, higherPoint.y, baseZ);
    return [cornerPoint, higherPoint.clone()];
  }, [pickedPoints]);

  const textPosition = useMemo((): [number, number, number] => {
    if (pickedPoints.length < 2) return [0, 0, 0];
    const mid = pickedPoints[0].clone().add(pickedPoints[1]).multiplyScalar(0.5);
    return mid.toArray() as [number, number, number];
  }, [pickedPoints]);

  const initialFontSize = useMemo(() => {
    if (pickedPoints.length < 2) return 0;
    const pos = new THREE.Vector3(...textPosition);
    return getUnitsPerPixel(camera, renderer, pos) * 16.8;
  }, [textPosition, pickedPoints, camera, renderer]);

  const heightTextPosition = useMemo((): [number, number, number] => {
    if (pickedPoints.length < 2) return [0, 0, 0];
    const baseZ = Math.min(pickedPoints[0].z, pickedPoints[1].z);
    const higherPoint = pickedPoints[0].z < pickedPoints[1].z ? pickedPoints[1] : pickedPoints[0];
    const cornerPoint = new THREE.Vector3(higherPoint.x, higherPoint.y, baseZ);
    return cornerPoint.clone().add(higherPoint).multiplyScalar(0.5).toArray() as [number, number, number];
  }, [pickedPoints]);

  const heightDifference = useMemo(() => {
    if (pickedPoints.length < 2) return 0;
    return Math.abs(pickedPoints[1].z - pickedPoints[0].z);
  }, [pickedPoints]);

  const distance2D = useMemo(() => {
    if (pickedPoints.length < 2) return 0;
    const dx = pickedPoints[1].x - pickedPoints[0].x;
    const dy = pickedPoints[1].y - pickedPoints[0].y;
    return Math.sqrt(dx * dx + dy * dy);
  }, [pickedPoints]);

  const distance2DTextPosition = useMemo((): [number, number, number] => {
    if (pickedPoints.length < 2) return [0, 0, 0];
    const baseZ = Math.min(pickedPoints[0].z, pickedPoints[1].z);
    const lowerPoint = pickedPoints[0].z < pickedPoints[1].z ? pickedPoints[0] : pickedPoints[1];
    const higherPoint = pickedPoints[0].z < pickedPoints[1].z ? pickedPoints[1] : pickedPoints[0];
    const cornerPoint = new THREE.Vector3(higherPoint.x, higherPoint.y, baseZ);
    return lowerPoint.clone().add(cornerPoint).multiplyScalar(0.5).toArray() as [number, number, number];
  }, [pickedPoints]);

  const isOrtho = camera.type === 'OrthographicCamera';
  const showHeightVisualization = heightDifference >= 0.05 && !isOrtho;

  const labelsAreTooClose = useMemo(() => {
    if (pickedPoints.length < 2 || !showHeightVisualization) return false;
    return (
      new THREE.Vector3(...textPosition).distanceTo(new THREE.Vector3(...heightTextPosition)) < initialFontSize * 5
    );
  }, [textPosition, heightTextPosition, initialFontSize, pickedPoints, showHeightVisualization]);

  const calculateScale = useCallback(
    (point: THREE.Vector3) => 20 * getUnitsPerPixel(camera, renderer, point),
    [camera, renderer],
  );

  const pointGroupRefs = useRef<(THREE.Group | null)[]>([]);
  const hoverGroupRef = useRef<THREE.Group | null>(null);
  const textRefs = useRef<{ main: any; height: any; dist2D: any }>({ main: null, height: null, dist2D: null });

  useFrame(() => {
    pickedPoints.forEach((point, i) => {
      const group = pointGroupRefs.current[i];
      if (group) {
        const s = calculateScale(point);
        group.scale.setScalar(s);
      }
    });

    if (hoverGroupRef.current && hoverPoint) {
      const s = calculateScale(hoverPoint);
      hoverGroupRef.current.scale.setScalar(s);
    }

    if (pickedPoints.length === 2) {
      const mid = pickedPoints[0].clone().add(pickedPoints[1]).multiplyScalar(0.5);
      const fs = getUnitsPerPixel(camera, renderer, mid) * 16.8;
      if (textRefs.current.main) textRefs.current.main.fontSize = fs;
      if (textRefs.current.height) textRefs.current.height.fontSize = fs;
      if (textRefs.current.dist2D) textRefs.current.dist2D.fontSize = fs;
    }
  });

  if (!showMeasurements) return null;

  return (
    <>
      {/* Hover preview marker - semi-transparent */}
      {hoverPoint && !draggingRef.current && (
        <group ref={hoverGroupRef} position={hoverPoint.toArray()} scale={calculateScale(hoverPoint)}>
          <PointX scale={1} position={new THREE.Vector3()} />
        </group>
      )}

      {pickedPoints.map((point, i) => (
        <group
          key={i}
          ref={(el) => {
            pointGroupRefs.current[i] = el;
          }}
          position={point.toArray()}
          scale={calculateScale(point)}
          onPointerDown={(e) => startDrag(e, i)}>
          <PointX scale={1} position={new THREE.Vector3()} />
        </group>
      ))}

      {pickedPoints.length === 2 && (
        <>
          {/* Main distance line */}
          <CylinderLine start={pickedPoints[0]} end={pickedPoints[1]} color='red' />

          {showHeightVisualization && !draggingRef.current && (
            <>
              {/* Triangle visualization for height */}
              <CylinderLine start={trianglePoints[0]} end={trianglePoints[1]} color='blue' opacity={0.5} />
              <CylinderLine start={trianglePoints[1]} end={trianglePoints[2]} color='blue' opacity={0.5} />

              {/* Vertical height line */}
              <CylinderLine start={heightLinePoints[0]} end={heightLinePoints[1]} color='green' />
            </>
          )}

          {!draggingRef.current ? (
            isOrtho ? (
              <Billboard position={textPosition}>
                <Text
                  ref={(el) => {
                    textRefs.current.main = el;
                  }}
                  fontSize={initialFontSize}
                  color='white'
                  outlineColor='black'
                  outlineWidth={0.01}
                  outlineBlur={0.01}
                  strokeColor='red'
                  strokeWidth={0.001}
                  depthOffset={Number.MIN_SAFE_INTEGER}
                  renderOrder={Number.MAX_SAFE_INTEGER}
                  anchorX='center'
                  anchorY='middle'>
                  {pickedPoints[0].distanceTo(pickedPoints[1]).toFixed(2)}
                </Text>
              </Billboard>
            ) : labelsAreTooClose ? (
              <Billboard position={textPosition}>
                <Text
                  ref={(el) => {
                    textRefs.current.main = el;
                  }}
                  fontSize={initialFontSize}
                  color='white'
                  outlineColor='black'
                  outlineWidth={0.01}
                  outlineBlur={0.01}
                  strokeColor='red'
                  strokeWidth={0.001}
                  depthOffset={Number.MIN_SAFE_INTEGER}
                  renderOrder={Number.MAX_SAFE_INTEGER}
                  anchorX='center'
                  anchorY='middle'>
                  3D – {pickedPoints[0].distanceTo(pickedPoints[1]).toFixed(2)} | 2D – {distance2D.toFixed(2)} |{' '}
                  {'H – '}
                  {heightDifference.toFixed(2)}
                </Text>
              </Billboard>
            ) : (
              <>
                <Billboard position={textPosition}>
                  <Text
                    ref={(el) => {
                      textRefs.current.main = el;
                    }}
                    fontSize={initialFontSize}
                    color='white'
                    outlineColor='black'
                    outlineWidth={0.01}
                    outlineBlur={0.01}
                    strokeColor='red'
                    strokeWidth={0.001}
                    depthOffset={Number.MIN_SAFE_INTEGER}
                    renderOrder={Number.MAX_SAFE_INTEGER}
                    anchorX='center'
                    anchorY='middle'>
                    3D – {pickedPoints[0].distanceTo(pickedPoints[1]).toFixed(2)}
                  </Text>
                </Billboard>
                {showHeightVisualization && (
                  <>
                    <Billboard position={heightTextPosition}>
                      <Text
                        ref={(el) => {
                          textRefs.current.height = el;
                        }}
                        fontSize={initialFontSize}
                        color='white'
                        outlineColor='black'
                        outlineWidth={0.01}
                        outlineBlur={0.01}
                        strokeColor='green'
                        strokeWidth={0.001}
                        depthOffset={Number.MIN_SAFE_INTEGER}
                        renderOrder={Number.MAX_SAFE_INTEGER}
                        anchorX='center'
                        anchorY='middle'>
                        H – {heightDifference.toFixed(2)}
                      </Text>
                    </Billboard>
                    <Billboard position={distance2DTextPosition}>
                      <Text
                        ref={(el) => {
                          textRefs.current.dist2D = el;
                        }}
                        fontSize={initialFontSize}
                        color='white'
                        outlineColor='black'
                        outlineWidth={0.01}
                        outlineBlur={0.01}
                        strokeColor='blue'
                        strokeWidth={0.001}
                        depthOffset={Number.MIN_SAFE_INTEGER}
                        renderOrder={Number.MAX_SAFE_INTEGER}
                        anchorX='center'
                        anchorY='middle'>
                        2D – {distance2D.toFixed(2)}
                      </Text>
                    </Billboard>
                  </>
                )}
              </>
            )
          ) : null}
        </>
      )}
    </>
  );
};

export default MeasuringTool;
